/**
 * Speurtocht List Screen
 * Show available speurtochts for the current weekend
 */

import React, { useState, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  ActivityIndicator,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import {
  useSpeurtochts,
  SpeurttochtWithSession,
} from "@/modules/speurtocht";

/**
 * Status badge configuration
 */
const getStatusConfig = (speurtocht: SpeurttochtWithSession) => {
  if (speurtocht.session?.status === "completed") {
    return { label: "Voltooid", color: "#10B981", icon: "checkmark-circle" };
  }
  if (speurtocht.session?.status === "active") {
    return { label: "Bezig", color: "#3B82F6", icon: "play-circle" };
  }
  if (speurtocht.status === "active" && speurtocht.can_start) {
    return { label: "Beschikbaar", color: "#F59E0B", icon: "flag" };
  }
  return { label: "Niet beschikbaar", color: "#6B7280", icon: "lock-closed" };
};

export default function SpeurttochtListScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { activeWeekend, userTeam, roles } = useAuth();
  const { speurtochts, isLoading, error, refresh } = useSpeurtochts();

  const [isRefreshing, setIsRefreshing] = useState(false);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await refresh();
    setIsRefreshing(false);
  }, [refresh]);

  /**
   * Handle speurtocht press
   */
  const handlePress = (speurtocht: SpeurttochtWithSession) => {
    router.push(`/(tabs)/games/speurtocht/${speurtocht.id}`);
  };

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "left", "right"]}>
      {/* Header */}
      <View className="px-4 py-4 flex-row items-center justify-between">
        <View className="flex-row items-center flex-1">
          <TouchableOpacity
            onPress={() => router.back()}
            className="w-10 h-10 rounded-full items-center justify-center mr-3"
            style={{ backgroundColor: colors.surface }}
            accessibilityRole="button"
            accessibilityLabel="Terug"
          >
            <Ionicons name="arrow-back" size={22} color={colors.text} />
          </TouchableOpacity>
          <View className="flex-1">
            <Text className="text-xl font-bold" style={{ color: colors.text }}>
              Speurtochten
            </Text>
            {activeWeekend && (
              <Text className="text-sm" style={{ color: colors.textSecondary }}>
                {activeWeekend.name}
              </Text>
            )}
          </View>
        </View>
        {roles.isFamilieAdmin && (
          <TouchableOpacity
            onPress={() => router.push("/(admin)/games/speurtocht")}
            className="w-10 h-10 rounded-full items-center justify-center"
            style={{ backgroundColor: "#10B981" }}
            accessibilityRole="button"
            accessibilityLabel="Beheer speurtochten"
          >
            <Ionicons name="settings-outline" size={20} color="#FFFFFF" />
          </TouchableOpacity>
        )}
      </View>

      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
      >
        {isLoading ? (
          <View className="py-12 items-center">
            <ActivityIndicator size="large" color="#10B981" />
          </View>
        ) : !activeWeekend ? (
          <View className="py-12 items-center px-4">
            <View
              className="w-20 h-20 rounded-full items-center justify-center mb-4"
              style={{ backgroundColor: "#10B98120" }}
            >
              <Ionicons name="calendar-outline" size={40} color="#10B981" />
            </View>
            <Text className="text-lg font-semibold mb-2" style={{ color: colors.text }}>
              Geen actief weekend
            </Text>
            <Text className="text-center" style={{ color: colors.textSecondary }}>
              Speurtochten zijn alleen beschikbaar tijdens een actief weekend
            </Text>
          </View>
        ) : !userTeam ? (
          <View className="py-12 items-center px-4">
            <View
              className="w-20 h-20 rounded-full items-center justify-center mb-4"
              style={{ backgroundColor: "#10B98120" }}
            >
              <Ionicons name="people-outline" size={40} color="#10B981" />
            </View>
            <Text className="text-lg font-semibold mb-2" style={{ color: colors.text }}>
              Geen team
            </Text>
            <Text className="text-center" style={{ color: colors.textSecondary }}>
              Je moet in een team zitten om mee te doen aan speurtochten
            </Text>
          </View>
        ) : speurtochts.length === 0 ? (
          <View className="py-12 items-center px-4">
            <View
              className="w-20 h-20 rounded-full items-center justify-center mb-4"
              style={{ backgroundColor: "#10B98120" }}
            >
              <Ionicons name="map-outline" size={40} color="#10B981" />
            </View>
            <Text className="text-lg font-semibold mb-2" style={{ color: colors.text }}>
              Geen speurtochten gevonden
            </Text>
            <Text className="text-center mb-4" style={{ color: colors.textSecondary }}>
              {roles.isFamilieAdmin 
                ? "Maak je eerste speurtocht aan via de admin"
                : "Er zijn nog geen speurtochten beschikbaar voor dit weekend"
              }
            </Text>
            {roles.isFamilieAdmin && (
              <TouchableOpacity
                onPress={() => router.push("/(admin)/games/speurtocht")}
                className="px-6 py-3 rounded-xl flex-row items-center"
                style={{ backgroundColor: "#10B981" }}
              >
                <Ionicons name="add" size={20} color="#FFFFFF" />
                <Text className="ml-2 font-bold text-white">Speurtocht Beheren</Text>
              </TouchableOpacity>
            )}
          </View>
        ) : (
          <View className="px-4 pb-4">
            {/* Team Banner */}
            {userTeam && (
              <View
                className="rounded-xl p-3 mb-4 flex-row items-center"
                style={{ backgroundColor: colors.primaryLight }}
              >
                <View
                  className="w-10 h-10 rounded-full items-center justify-center mr-3"
                  style={{ backgroundColor: userTeam.color || colors.primary }}
                >
                  <Ionicons name="people" size={20} color="#FFFFFF" />
                </View>
                <View className="flex-1">
                  <Text className="font-semibold" style={{ color: colors.text }}>
                    Team {userTeam.name}
                  </Text>
                  <Text className="text-sm" style={{ color: colors.textSecondary }}>
                    Werk samen met je teamleden!
                  </Text>
                </View>
              </View>
            )}

            {/* Speurtocht Cards */}
            {speurtochts.map((speurtocht) => {
              const statusConfig = getStatusConfig(speurtocht);
              const isActive = speurtocht.status === "active";
              const hasSession = !!speurtocht.session;
              const isCompleted = speurtocht.session?.status === "completed";
              const isInProgress = speurtocht.session?.status === "active";

              return (
                <TouchableOpacity
                  key={speurtocht.id}
                  onPress={() => handlePress(speurtocht)}
                  activeOpacity={0.7}
                  className="rounded-xl mb-3 overflow-hidden"
                  style={{
                    backgroundColor: isInProgress
                      ? `${speurtocht.color}15`
                      : colors.surface,
                    borderWidth: isInProgress ? 2 : 1,
                    borderColor: isInProgress ? speurtocht.color : colors.border,
                    opacity: isActive ? 1 : 0.6,
                  }}
                >
                  <View className="p-4">
                    <View className="flex-row items-start">
                      {/* Icon */}
                      <View
                        className="w-14 h-14 rounded-xl items-center justify-center mr-3"
                        style={{ backgroundColor: `${speurtocht.color}25` }}
                      >
                        <Ionicons
                          name={speurtocht.icon as any}
                          size={28}
                          color={speurtocht.color}
                        />
                      </View>

                      {/* Info */}
                      <View className="flex-1">
                        <Text
                          className="font-bold text-lg"
                          style={{ color: colors.text }}
                        >
                          {speurtocht.name}
                        </Text>
                        {speurtocht.description && (
                          <Text
                            className="text-sm mt-0.5"
                            style={{ color: colors.textSecondary }}
                            numberOfLines={2}
                          >
                            {speurtocht.description}
                          </Text>
                        )}

                        {/* Stats */}
                        <View className="flex-row items-center mt-2 flex-wrap">
                          <View
                            className="flex-row items-center px-2 py-1 rounded mr-2"
                            style={{ backgroundColor: colors.surfaceSecondary }}
                          >
                            <Ionicons
                              name="footsteps"
                              size={12}
                              color={colors.textSecondary}
                            />
                            <Text
                              className="text-xs ml-1"
                              style={{ color: colors.textSecondary }}
                            >
                              {speurtocht.steps_count || 0} stappen
                            </Text>
                          </View>

                          {speurtocht.has_time_limit &&
                            speurtocht.max_duration_minutes && (
                              <View
                                className="flex-row items-center px-2 py-1 rounded mr-2"
                                style={{ backgroundColor: colors.surfaceSecondary }}
                              >
                                <Ionicons
                                  name="time"
                                  size={12}
                                  color={colors.textSecondary}
                                />
                                <Text
                                  className="text-xs ml-1"
                                  style={{ color: colors.textSecondary }}
                                >
                                  {speurtocht.max_duration_minutes} min
                                </Text>
                              </View>
                            )}

                          {/* Status Badge */}
                          <View
                            className="flex-row items-center px-2 py-1 rounded"
                            style={{ backgroundColor: `${statusConfig.color}20` }}
                          >
                            <Ionicons
                              name={statusConfig.icon as any}
                              size={12}
                              color={statusConfig.color}
                            />
                            <Text
                              className="text-xs ml-1 font-medium"
                              style={{ color: statusConfig.color }}
                            >
                              {statusConfig.label}
                            </Text>
                          </View>
                        </View>

                        {/* Progress for active sessions */}
                        {isInProgress && speurtocht.session && (
                          <View className="mt-3">
                            <View className="flex-row justify-between mb-1">
                              <Text
                                className="text-xs"
                                style={{ color: colors.textSecondary }}
                              >
                                Voortgang
                              </Text>
                              <Text
                                className="text-xs font-medium"
                                style={{ color: speurtocht.color }}
                              >
                                {speurtocht.session.completed_steps}/
                                {speurtocht.session.total_steps}
                              </Text>
                            </View>
                            <View
                              className="h-2 rounded-full"
                              style={{ backgroundColor: colors.surfaceSecondary }}
                            >
                              <View
                                className="h-2 rounded-full"
                                style={{
                                  backgroundColor: speurtocht.color,
                                  width: `${
                                    (speurtocht.session.completed_steps /
                                      speurtocht.session.total_steps) *
                                    100
                                  }%`,
                                }}
                              />
                            </View>
                          </View>
                        )}

                        {/* Completed info */}
                        {isCompleted && speurtocht.session && (
                          <View
                            className="mt-3 flex-row items-center"
                            style={{
                              backgroundColor: "#10B98120",
                              padding: 8,
                              borderRadius: 8,
                            }}
                          >
                            <Ionicons
                              name="trophy"
                              size={16}
                              color="#10B981"
                            />
                            <Text
                              className="text-sm ml-2 font-medium"
                              style={{ color: "#10B981" }}
                            >
                              {speurtocht.session.total_points} punten behaald!
                            </Text>
                          </View>
                        )}
                      </View>

                      {/* Chevron */}
                      <Ionicons
                        name="chevron-forward"
                        size={20}
                        color={colors.textTertiary}
                        style={{ marginTop: 4 }}
                      />
                    </View>
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        )}

        <View className="h-24" />
      </ScrollView>
    </SafeAreaView>
  );
}

